/* ============================================================
   AFFILIATES  (/affiliate-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Rupa baharu bagi /affiliate. Susunan mengikut halaman sedia
   ada: ringkasan angka di atas, pautan perkongsian di tengah,
   senarai rujukan di bawah.

   ⛔ Pautan rujukan MESTI kekal bentuk `?ref=KOD`. Cookie
   `sesaat_ref` merentas sesaat.ai ↔ app.sesaat.ai bergantung
   padanya; tukar bentuk pautan = komisen ejen hilang senyap.
   Lihat nota Affiliate Ref Capture dalam CLAUDE.md.

   ⚠️ PROTOTAIP — angka dan senarai di bawah adalah contoh tetap.
   ============================================================ */

import { useState } from 'react';
import { Banknote, Clock, MousePointerClick, Target, TrendingUp, Copy, Check, Wallet } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, Card, CardBody, StatTile } from '../design/ui';
import { useT } from '../i18n';

const KOD = 'AFR-TNPXF9';

/* ⛔ Alamat pautan KEKAL bentuk `?ref=KOD` — lihat nota di kepala fail.
   Label + penerangan pula diterjemah pada masa render (pemalar aras-modul
   tidak boleh memanggil useT), jadi di sini hanya kunci kamus disimpan. */
const PAUTAN = [
    { key: 'salespage', label: 'affiliate.link.salespage', desc: 'affiliate.link.salespageDesc', url: `https://sesaat.ai/?ref=${KOD}` },
    { key: 'pricing', label: 'affiliate.link.pricing', desc: 'affiliate.link.pricingDesc', url: `https://sesaat.ai/pricing?ref=${KOD}` },
    { key: 'signup', label: 'affiliate.link.signup', desc: 'affiliate.link.signupDesc', url: `https://app.sesaat.ai/?ref=${KOD}` },
];

const RUJUKAN = [
    { email: 'aliaqistina***@gmail.com', pakej: 'Premium', tarikh: '5 Ogos 2026', komisen: 'RM 99.50', status: 'ok' },
    { email: 'nurhidayah***@gmail.com', pakej: 'Fighter', tarikh: '3 Ogos 2026', komisen: 'RM 399.50', status: 'ok' },
    { email: 'faezmegat***@gmail.com', pakej: 'Premium', tarikh: '1 Ogos 2026', komisen: 'RM 99.50', status: 'tunggu' },
    { email: 'wahida***@gmail.com', pakej: 'Premium + FLOWNITY', tarikh: '28 Julai 2026', komisen: 'RM 133.00', status: 'ok' },
];

export default function AffiliateV39View() {
    const t = useT();
    const [tab, setTab] = useState('dashboard');
    const [disalin, setDisalin] = useState(null);

    const salin = (key, url) => {
        navigator.clipboard?.writeText(url);
        setDisalin(key);
        setTimeout(() => setDisalin((k) => (k === key ? null : k)), 1600);
    };

    /* Dibina dalam komponen supaya label boleh diterjemah. Angka kekal
       nilai contoh — ia bukan kandungan kamus. */
    const STATS = [
        { label: t('affiliate.stat.available'), value: 'RM 1,284.50', hint: t('affiliate.stat.availableHint'), icon: <Banknote size={15} strokeWidth={1.8} /> },
        { label: t('affiliate.stat.pending'), value: 'RM 318.00', hint: t('affiliate.stat.pendingHint'), icon: <Clock size={15} strokeWidth={1.8} /> },
        { label: t('affiliate.stat.clicks'), value: '2,417', icon: <MousePointerClick size={15} strokeWidth={1.8} /> },
        { label: t('affiliate.stat.referrals'), value: '86', icon: <Target size={15} strokeWidth={1.8} /> },
        { label: t('affiliate.stat.conversion'), value: '3.6%', hint: t('affiliate.stat.conversionHint'), icon: <TrendingUp size={15} strokeWidth={1.8} /> },
    ];

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            <PageHeader
                title={t('affiliate.title')}
                subtitle={t('affiliate.subtitle')}
                actions={
                    <>
                        <Segmented
                            value={tab}
                            onChange={setTab}
                            options={[
                                { value: 'dashboard', label: t('affiliate.tabDashboard') },
                                { value: 'payouts', label: t('affiliate.tabPayouts') },
                            ]}
                        />
                        <Btn icon={<Wallet size={15} strokeWidth={2} />}>{t('affiliate.requestPayout')}</Btn>
                    </>
                }
            />

            {tab === 'dashboard' ? (
                <>
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                        gap: 12, marginBottom: 26,
                    }}>
                        {STATS.map((s) => <StatTile key={s.label} {...s} />)}
                    </div>

                    <h2 className="s-title" style={{ fontSize: 16, marginBottom: 4 }}>{t('affiliate.linksTitle')}</h2>
                    <p className="s-meta" style={{ fontSize: 13, marginBottom: 10 }}>
                        {t('affiliate.codeLine', { kod: KOD })} {t('affiliate.codeHint')}
                    </p>

                    <div style={{ display: 'grid', gap: 10, marginBottom: 26 }}>
                        {PAUTAN.map((p) => (
                            <Card key={p.key}>
                                <CardBody style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 220 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                            <h3 className="s-cardname">{t(p.label)}</h3>
                                        </div>
                                        <p className="s-meta" style={{ fontSize: 13, marginBottom: 6 }}>{t(p.desc)}</p>
                                        <code className="s-num" style={{
                                            display: 'inline-block', padding: '4px 8px',
                                            background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                                            borderRadius: 'var(--s-r-sm)', wordBreak: 'break-all',
                                        }}>{p.url}</code>
                                    </div>
                                    <Btn
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => salin(p.key, p.url)}
                                        icon={disalin === p.key
                                            ? <Check size={14} strokeWidth={2} />
                                            : <Copy size={14} strokeWidth={2} />}
                                    >
                                        {disalin === p.key ? t('affiliate.copied') : t('affiliate.copy')}
                                    </Btn>
                                </CardBody>
                            </Card>
                        ))}
                    </div>

                    <h2 className="s-title" style={{ fontSize: 16, marginBottom: 10 }}>{t('affiliate.recentTitle')}</h2>
                    <div className="s-list" style={{ overflowX: 'auto' }}>
                        <table className="s-table">
                            <thead>
                                <tr>
                                    <th>{t('affiliate.th.customer')}</th><th>{t('affiliate.th.package')}</th><th>{t('affiliate.th.date')}</th>
                                    <th>{t('common.status')}</th><th className="s-td-num">{t('affiliate.th.commission')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {RUJUKAN.map((r) => (
                                    <tr key={r.email}>
                                        <td className="s-td-strong">{r.email}</td>
                                        <td>{r.pakej}</td>
                                        <td>{r.tarikh}</td>
                                        <td>
                                            {r.status === 'ok'
                                                ? <Badge tone="ok">{t('affiliate.badge.credited')}</Badge>
                                                : <Badge tone="warn">{t('affiliate.badge.pending')}</Badge>}
                                        </td>
                                        <td className="s-td-num s-td-strong">{r.komisen}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <>
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                        gap: 12, marginBottom: 26,
                    }}>
                        <StatTile label={t('affiliate.payout.earnings')} value="RM 4,860.00" hint={t('affiliate.payout.earningsHint')} />
                        <StatTile label={t('affiliate.payout.withdrawable')} value="RM 1,284.50" hint={t('affiliate.payout.minHint', { jumlah: 'RM 100' })} />
                        <StatTile label={t('affiliate.payout.paid')} value="RM 3,257.50" hint={t('affiliate.payout.countHint', { n: 7 })} />
                    </div>

                    <h2 className="s-title" style={{ fontSize: 16, marginBottom: 10 }}>{t('affiliate.historyTitle')}</h2>
                    <div className="s-list" style={{ overflowX: 'auto' }}>
                        <table className="s-table">
                            <thead>
                                <tr><th>{t('affiliate.th.date')}</th><th>{t('affiliate.th.method')}</th><th>{t('common.status')}</th><th className="s-td-num">{t('affiliate.th.amount')}</th></tr>
                            </thead>
                            <tbody>
                                <tr><td className="s-td-strong">2 Ogos 2026</td><td>Maybank ···4821</td><td><Badge tone="ok">{t('affiliate.badge.done')}</Badge></td><td className="s-td-num s-td-strong">RM 780.00</td></tr>
                                <tr><td className="s-td-strong">2 Julai 2026</td><td>Maybank ···4821</td><td><Badge tone="ok">{t('affiliate.badge.done')}</Badge></td><td className="s-td-num s-td-strong">RM 1,120.00</td></tr>
                                <tr><td className="s-td-strong">2 Jun 2026</td><td>Maybank ···4821</td><td><Badge tone="ok">{t('affiliate.badge.done')}</Badge></td><td className="s-td-num s-td-strong">RM 640.50</td></tr>
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <p className="s-dim" style={{ marginTop: 22 }}>{t('affiliate.prototypeNote')}</p>
        </div>
    );
}
